import { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Search, Loader2, X, Plus, Lock } from 'lucide-react';
import api from '@host/axios';

import { Trans, useLingui } from '@lingui/react/macro';
// Chọn một người là phải thấy ngay mấy giờ bên chỗ họ — bắt người giao việc tự nhẩm
// "Toronto giờ là mấy" là cách chắc chắn để có những cái hẹn sai giờ.
import { motDongVeMui } from '../../utils/zoneClock';
import { msg } from '@lingui/core/macro';
import { i18n } from '../../i18n/runtime.js';

const FALLBACK = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect fill="%23e5e7eb" width="32" height="32" rx="16"/><text x="16" y="20" text-anchor="middle" fill="%236b7280" font-size="14">?</text></svg>';

function useUserSearch(open, search) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get('/users/simple', { params: { search: search || undefined, is_active: true, limit: 20 } })
        .then(r => setResults(r.data?.data || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, search ? 300 : 0);
    return () => clearTimeout(timer.current);
  }, [open, search]);
  return { results, loading };
}

// Gợi ý người để ADD nhanh vào task (participant task cha / hay cộng tác / cùng nhóm).
// suggest = { parentTaskId? } hoặc falsy (không gợi ý). Fetch 1 lần khi mở popup; row đã chọn
// được lọc phía client (theo value) nên không cần refetch mỗi lần toggle. CHỈ gợi ý, KHÔNG auto-add.
function useSuggestedPeople(open, suggest) {
  const [suggestions, setSuggestions] = useState([]);
  const parentTaskId = suggest?.parentTaskId || null;
  useEffect(() => {
    if (!open || !suggest) { setSuggestions([]); return; }
    let cancelled = false;
    const params = { limit: 8 };
    if (parentTaskId) params.parent_task_id = parentTaskId;
    api.get('/tasks/people-suggestions', { params })
      .then(r => { if (!cancelled) setSuggestions(r.data?.data || []); })
      .catch(() => { if (!cancelled) setSuggestions([]); });
    return () => { cancelled = true; };
  }, [open, parentTaskId, !!suggest]);
  return suggestions;
}

// Cache user info by ID — avoid extra API calls for display
const userCache = {};
// Export để callers (vd TaskCreateForm) có thể pre-seed currentUser ngay khi mount,
// tránh race condition: fetch /users/simple?ids=... fail/empty → bubble kẹt ở stub.
export function seedUserCache(user) {
  if (!user?.id) return;
  const id = user.id;
  userCache[id] = {
    ...userCache[id],
    id,
    name: user.name || user.display_name || user.email?.split('@')[0] || 'User',
    email: user.email || '',
    avatar: user.avatar || null,
    is_active: user.is_active !== false,
  };
}
function useCachedUser(userId) {
  const [user, setUser] = useState(userCache[userId] || null);
  useEffect(() => {
    if (!userId) { setUser(null); return; }
    if (userCache[userId]) { setUser(userCache[userId]); return; }
    // Fetch chính xác theo id — tránh trường hợp owner không nằm trong top 200 user (limit=200 cũ)
    api.get('/users/simple', { params: { ids: userId, limit: 1 } }).then(r => {
      (r.data?.data || []).forEach(u => { userCache[u.id] = u; });
      setUser(userCache[userId] || null);
    }).catch(() => {});
  }, [userId]);
  return user;
}

function useCachedUsers(userIds) {
  // CRITICAL: helper PHẢI luôn return array với độ dài = userIds.length.
  // Mỗi phần tử là user thật (cache hit) HOẶC stub {id, name: id.slice(0,2).toUpperCase()}.
  // KHÔNG bao giờ filter(Boolean) — sẽ làm bubble biến mất khi cache miss.
  const buildResult = (ids) => (ids || []).map(id => userCache[id] || { id, name: String(id || '?').slice(0, 2).toUpperCase() });
  const [users, setUsers] = useState(() => buildResult(userIds));
  useEffect(() => {
    if (!userIds?.length) { setUsers([]); return; }
    const missing = userIds.filter(id => !userCache[id]);
    if (missing.length === 0) {
      setUsers(buildResult(userIds));
      return;
    }
    // Fetch theo ids cụ thể — không dùng limit=200 vì có thể user không nằm trong 200 đầu
    api.get('/users/simple', { params: { ids: missing.join(','), limit: missing.length } }).then(r => {
      (r.data?.data || []).forEach(u => { userCache[u.id] = u; });
      // Fallback: nếu API không support filter ids, fetch all với limit lớn
      const stillMissing = userIds.filter(id => !userCache[id]);
      if (stillMissing.length > 0) {
        api.get('/users/simple', { params: { limit: 1000 } }).then(r2 => {
          (r2.data?.data || []).forEach(u => { userCache[u.id] = u; });
        }).catch(() => {}).finally(() => setUsers(buildResult(userIds)));
      } else {
        setUsers(buildResult(userIds));
      }
    }).catch(() => {
      // Fallback bigger limit
      api.get('/users/simple', { params: { limit: 1000 } }).then(r => {
        (r.data?.data || []).forEach(u => { userCache[u.id] = u; });
      }).catch(() => {}).finally(() => setUsers(buildResult(userIds)));
    });
  }, [userIds?.join(',')]);
  return users;
}

function UserRow({ u, selected, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-blue-50 text-left text-sm transition ${selected ? 'bg-blue-50' : ''}`}>
      {u.avatar
        ? <img src={u.avatar} className="w-7 h-7 rounded-full object-cover shrink-0" alt={u.name}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
        : null}
      <div className={`w-7 h-7 rounded-full bg-indigo-600 text-white items-center justify-center text-[11px] font-bold shrink-0 ${u.avatar ? 'hidden' : 'flex'}`}>
        {u.name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-900 truncate">{u.name}</p>
        <p className="text-[10px] text-gray-400 truncate">
          {u.department || u.position || u.email || ''}
          {motDongVeMui(u.timezone, u.work_city) && (
            <span className="text-amber-700"> · {motDongVeMui(u.timezone, u.work_city)}</span>
          )}
        </p>
      </div>
      {selected && <span className="text-blue-600 shrink-0 text-xs">✓</span>}
    </button>
  );
}

function getDropdownPos(ref) {
  if (!ref.current) return {};
  const r = ref.current.getBoundingClientRect();
  return { top: r.bottom + 4, left: Math.max(r.left - 100, 8) };
}

/**
 * @param {boolean} [disabled] — chỉ hiển thị, không mở được popup chọn người.
 *   Khác `onChange` no-op: no-op vẫn mở dropdown + vẫn gọi /users/simple, user bấm
 *   chọn mà không có gì xảy ra ⇒ trông như lỗi. Dùng cho ô bị khoá (vd "Người thực
 *   hiện" của mẫu phê duyệt bật `assignee_self_only`).
 * @param {string} [lockHint] — tooltip giải thích vì sao khoá.
 */
export function OwnerPickerMini({ value, onChange, placeholder = i18n._(msg`Chưa gán`), disabled = false, lockHint }) {
  const {
    t
  } = useLingui();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const cachedOwner = useCachedUser(value);
  const { results, loading } = useUserSearch(open, search);

  // Refresh fresh data cho owner hiện tại khi mở popup (để hiện đúng is_active mới nhất)
  const [freshOwner, setFreshOwner] = useState(null);
  useEffect(() => {
    if (!open || !value) { setFreshOwner(null); return; }
    api.get('/users/simple', { params: { ids: value, limit: 1 } })
      .then(r => {
        const u = (r.data?.data || [])[0] || null;
        if (u) userCache[u.id] = u;
        setFreshOwner(u);
      })
      .catch(() => setFreshOwner(null));
  }, [open, value]);
  const owner = freshOwner || cachedOwner;
  const ownerInactive = owner && owner.is_active === false;

  const matchSearch = (u) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (u.name || '').toLowerCase().includes(q)
      || (u.department || '').toLowerCase().includes(q)
      || (u.position || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q);
  };
  // Section "Chọn người khác": active users, không phải owner hiện tại
  const candidates = results.filter(u => u.is_active !== false && u.id !== value && matchSearch(u));

  const pos = ref.current
    ? { top: ref.current.getBoundingClientRect().bottom + 4, left: Math.max(8, Math.min(window.innerWidth - 340 - 8, ref.current.getBoundingClientRect().left)) }
    : {};

  if (disabled) {
    return (
      <div className="flex items-center gap-2 px-2 py-1 rounded-lg border border-gray-200 bg-gray-50 w-fit"
        title={lockHint || undefined}>
        <div className={`w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0 overflow-hidden ${ownerInactive ? 'grayscale' : ''}`}>
          {owner?.avatar ? <img src={owner.avatar} title={owner.name} className="w-full h-full rounded-full object-cover" /> : owner?.name?.[0]?.toUpperCase() || '?'}
        </div>
        <span className={`text-sm truncate ${ownerInactive ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{owner?.name || placeholder}</span>
        <Lock size={12} className="text-gray-400 shrink-0" />
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => { setOpen(!open); setSearch(''); }}
        className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-blue-50 border border-transparent hover:border-blue-200 transition group cursor-pointer">
        <div className={`w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0 overflow-hidden ${ownerInactive ? 'grayscale' : ''}`}>
          {owner?.avatar ? <img src={owner.avatar} title={owner.name} className="w-full h-full rounded-full object-cover" /> : owner?.name?.[0]?.toUpperCase() || '?'}
        </div>
        <span className={`text-sm group-hover:text-blue-700 truncate ${ownerInactive ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{owner?.name || placeholder}</span>
        <ChevronDown size={12} className="text-gray-400 shrink-0" />
      </button>
      {open && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10010]" onClick={() => { setOpen(false); setSearch(''); }}>
          <div className="absolute w-[320px] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
            style={pos} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900"><Trans>Người sở hữu</Trans></h3>
              <button onClick={() => { setOpen(false); setSearch(''); }}
                className="p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
                <X size={16} />
              </button>
            </div>

            {/* Search */}
            <div className="px-4 py-2 border-b border-gray-100">
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} autoFocus
                placeholder={t`Tìm người...`}
                className="w-full px-2 py-1.5 text-sm border-0 focus:outline-none placeholder:text-gray-400 bg-transparent" />
            </div>

            <div className="max-h-[360px] overflow-y-auto">
              {/* Section: Đang sở hữu (click → bỏ gán) */}
              {owner && matchSearch(owner) && (
                <div>
                  <p className="px-4 pt-3 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider"><Trans>Đang sở hữu</Trans></p>
                  <OwnerRow u={owner} selected
                    onClick={() => { onChange(null); setOpen(false); setSearch(''); }}
                    actionLabel={t`Bỏ gán`} />
                </div>
              )}

              {/* Section: Chọn người khác */}
              <div>
                <p className="px-4 pt-3 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{owner ? t`Chuyển cho người khác` : t`Chọn người`}</p>
                {loading && <div className="py-3 flex justify-center"><Loader2 size={14} className="text-gray-400 animate-spin" /></div>}
                {!loading && candidates.length === 0 && (
                  <p className="text-center py-3 text-xs text-gray-400 italic">{search ? t`Không tìm thấy` : t`Hết rồi`}</p>
                )}
                {!loading && candidates.map(u => (
                  <OwnerRow key={u.id} u={u} onClick={() => { onChange(u.id); setOpen(false); setSearch(''); }} />
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function OwnerRow({ u, selected, onClick, actionLabel }) {
  const sub = u.department || u.position || u.email || '';
  // `Toronto · 09:14 · −11h`. Hiện ngay khi người này ĐÃ khai múi giờ — kể cả cùng múi với
  // mình (lúc đó phần lệch tự vắng mặt). Người chưa khai thì `null`, dòng phụ giữ nguyên như
  // cũ ⇒ hệ chưa bật múi giờ đa vùng không thấy gì khác. Tự gác bằng dữ liệu, không cần cờ.
  const dongMui = motDongVeMui(u.timezone, u.work_city);
  const inactive = u.is_active === false;
  return (
    <button type="button" onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left transition group ${selected ? 'bg-blue-50/40' : ''} ${inactive ? 'opacity-70' : ''}`}>
      {u.avatar
        ? <img src={u.avatar} className={`w-8 h-8 rounded-full object-cover shrink-0 ${inactive ? 'grayscale' : ''}`}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} alt={u.name} />
        : null}
      <div className={`w-8 h-8 rounded-full bg-indigo-600 text-white items-center justify-center text-xs font-bold shrink-0 ${inactive ? 'grayscale' : ''} ${u.avatar ? 'hidden' : 'flex'}`}>
        {u.name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 truncate flex items-center gap-1.5">
          {u.name}
          {inactive && (
            <span className="text-[9px] font-semibold uppercase px-1.5 py-px rounded bg-gray-200 text-gray-500 border border-gray-300 shrink-0"><Trans>Vô hiệu</Trans></span>
          )}
        </p>
        {(sub || dongMui) && (
          <p className="text-[11px] text-gray-400 truncate">
            {sub}
            {sub && dongMui ? ' · ' : ''}
            {dongMui && <span className="text-amber-700">{dongMui}</span>}
          </p>
        )}
      </div>
      {selected ? (
        actionLabel
          ? <span className="text-[10px] text-gray-300 group-hover:text-red-500 shrink-0 transition">{actionLabel}</span>
          : <span className="text-blue-600 shrink-0 text-xs">✓</span>
      ) : null}
    </button>
  );
}

/**
 * @param {string[]} [hideIds] - ẩn KHỎI PHẦN HIỂN THỊ (avatar + tên) những người này, thường là chủ
 *   sở hữu — vì `followers` luôn chứa cả owner nên gộp chung một ô "Sở hữu" sẽ bị lặp mặt hai lần.
 *   CHỈ ảnh hưởng hiển thị: `value` truyền cho dropdown/onChange vẫn nguyên vẹn, không thì bấm
 *   thêm/bớt một người sẽ vô tình xoá owner khỏi followers.
 * @param {boolean} [showNames] - hiện TÊN cạnh avatar. Mặc định TẮT: hàng "Sở hữu" nằm trên header
 *   cạnh nút Tạo Deal, hiện tên là chiếm hết diện tích (sếp Phú 31.07.2026: "gom lại, đang bị chiếm
 *   diện tích quá"). Ai là ai thì xem trong dropdown — chỗ có sẵn chỗ.
 * @param {string} [primaryId] - người phụ trách CHÍNH (owner_id): trong dropdown sẽ dán badge "Chính"
 *   để phân vai với đồng sở hữu, thay vì nhồi badge ra ngoài header.
 */
export function FollowersPickerMini({ value = [], onChange, currentUserId, labels, maxAvatars = 10, suggest, hideIds = [], showNames = false, primaryId, primaryName, onSetPrimary }) {
  const lb = {
    title: labels?.title || i18n._(msg`Người theo dõi`),
    current: labels?.current || i18n._(msg`Đang theo dõi`),
    add: labels?.add || i18n._(msg`Thêm người`),
  };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const cachedFollowers = useCachedUsers(value);
  const { results, loading } = useUserSearch(open, search);
  const suggestions = useSuggestedPeople(open, suggest);

  // Khi mở popup → refetch fresh data cho followers (đảm bảo is_active mới nhất, tránh cache stale)
  const [freshFollowers, setFreshFollowers] = useState(null);
  useEffect(() => {
    if (!open || !value.length) { setFreshFollowers(null); return; }
    api.get('/users/simple', { params: { ids: value.join(','), limit: value.length } })
      .then(r => {
        const list = r.data?.data || [];
        list.forEach(u => { userCache[u.id] = u; }); // refresh cache cho components khác
        setFreshFollowers(list);
      })
      .catch(() => setFreshFollowers(null));
  }, [open, value.join(',')]);
  // CRITICAL: freshFollowers=[] vẫn TRUTHY → `||` sẽ fallback đến []. Xem AssigneePickerPill bên dưới.
  const followers = (freshFollowers && freshFollowers.length) ? freshFollowers : cachedFollowers;
  const toggle = (id) => {
    value.includes(id) ? onChange(value.filter(x => x !== id)) : onChange([...value, id]);
    setSearch(''); // clear search sau khi pick để chọn người tiếp theo dễ
  };
  const isMeFollowing = currentUserId && value.includes(currentUserId);
  const MAX_SHOW = maxAvatars;
  // `shown` = phần HIỂN THỊ (đã trừ hideIds). `followers`/`value` giữ nguyên cho dropdown + onChange.
  const hideSet = new Set((hideIds || []).map(String));
  let shown = hideSet.size ? followers.filter(u => !hideSet.has(String(u.id))) : followers;
  // Chủ chính ĐỨNG ĐẦU cụm avatar. Nếu không sắp, avatar đầu tiên là một đồng sở hữu bất kỳ trong khi
  // chữ bên cạnh là TÊN CHỦ CHÍNH ⇒ mặt và tên lệch nhau, người xem hiểu sai ai là chủ (đã sa 31.07).
  if (primaryId) {
    const pid = String(primaryId);
    shown = [...shown].sort((a, b) => (String(a.id) === pid ? -1 : String(b.id) === pid ? 1 : 0));
  }
  const visible = shown.slice(0, MAX_SHOW);
  const overflow = shown.length - MAX_SHOW;

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center h-7 cursor-pointer" onClick={() => { setOpen(!open); setSearch(''); }}>
        {visible.map((u, i) => (
          <div key={u.id} className={`relative w-7 h-7 rounded-full border-2 border-white bg-indigo-600 text-white flex items-center justify-center text-[9px] font-bold shrink-0 overflow-hidden ${i > 0 ? '-ml-2' : ''} hover:ring-2 hover:ring-indigo-300 transition`}
            title={u.name + (u.department ? ' · ' + u.department : '')}>
            {/* ⚠️ PHẢI có onError (2 chỗ khác trong file này đã có, chỗ này thì quên): avatar Zalo
                `s120-ava-talk.zadn.vn` hay tải hỏng ⇒ trước đây ra khối tròn RỖNG, người dùng không
                biết ai đang đồng sở hữu (sếp Phú 31.07.2026: "đã bấm đồng ý nhưng chưa thấy test").
                Hỏng ảnh thì lộ chữ cái đầu ở lớp dưới. */}
            {u.avatar && <img src={u.avatar} title={u.name} className="w-full h-full rounded-full object-cover absolute"
              onError={e => { e.target.style.display = 'none'; }} />}
            {u.name?.[0]?.toUpperCase() || '?'}
          </div>
        ))}
        {overflow > 0 && (
          <div className="w-7 h-7 rounded-full border-2 border-white bg-gray-200 text-gray-600 flex items-center justify-center text-[9px] font-bold shrink-0 -ml-2"
            title={shown.slice(MAX_SHOW).map(u => u.name).join(', ')}>+{overflow}</div>
        )}
        {/* Avatar không nói được TÊN. Ít người thì hiện thẳng tên + badge vai trò — đó mới trả lời
            được "ai đang đồng sở hữu?" mà không phải rê chuột từng vòng tròn. */}
        {showNames && shown.length > 0 && shown.length <= 2 && (
          <span className="ml-1.5 text-[12px] text-gray-700 truncate max-w-[150px]"
            title={shown.map(u => u.name).join(', ')}>{shown.map(u => u.name).join(', ')}</span>
        )}
        {/* MỘT CONTROL DUY NHẤT cho ô "Sở hữu" (sếp Phú 31.07.2026: "đồng sở hữu là nằm trong cái sở
            hữu này chứ ko phải tách ra"). Trigger hiện TÊN chủ chính + "+N" đồng sở hữu; đổi chủ và
            thêm/bớt đồng sở hữu đều làm trong CÙNG dropdown. */}
        {/* Tên CHỈ hiện khi có ĐÚNG MỘT người (sếp Phú 31.07.2026: "từ 2 user trở lên thì cần bỏ tên
            ra"). Từ 2 người, avatar chồng đã nói đủ và tên thì chiếm diện tích header; ai là ai thì
            hover avatar hoặc mở dropdown (có badge Chính/Đồng). */}
        {primaryName && shown.length <= 1 && (
          <span className="ml-1.5 text-[13px] text-gray-800 truncate max-w-[150px]" title={primaryName}>{primaryName}</span>
        )}
        <div className={`w-7 h-7 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition shrink-0 ${shown.length > 0 ? 'ml-1.5' : ''}`}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M12 5v14M5 12h14"/></svg>
        </div>
      </div>
      {open && ReactDOM.createPortal(
        <FollowersDropdown
          triggerRef={ref}
          followers={followers}
          primaryId={primaryId}
          onSetPrimary={onSetPrimary}
          value={value}
          search={search}
          setSearch={setSearch}
          results={results}
          loading={loading}
          labels={lb}
          suggestions={suggestions}
          onClose={() => { setOpen(false); setSearch(''); }}
          onToggle={toggle}
        />,
        document.body,
      )}
    </div>
  );
}

/* AssigneePickerPill — variant của FollowersPickerMini với trigger là "pill" thay vì
 * avatar stack. Dùng cho UI cần show full tên+stage cho 1 user (vd modal Tạo task).
 *
 *   <AssigneePickerPill value={[...]} onChange={...} currentUserId={...}
 *     stageLabel="Chờ xử lý" stageColor="#9CA3AF" />
 *
 * Reuse FollowersDropdown popup nên UX search/add giống FollowersPickerMini.
 */
export function AssigneePickerPill({ value = [], onChange, currentUserId, stageLabel = i18n._(msg`Chờ xử lý`), stageColor = '#9CA3AF', labels, suggest }) {
  const {
    t
  } = useLingui();

  const lb = {
    title: labels?.title || t`Người thực hiện`,
    current: labels?.current || t`Đang thực hiện`,
    add: labels?.add || t`Thêm người`,
  };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const cachedUsers = useCachedUsers(value);
  const { results, loading } = useUserSearch(open, search);
  const suggestions = useSuggestedPeople(open, suggest);

  const [freshUsers, setFreshUsers] = useState(null);
  useEffect(() => {
    if (!open || !value.length) { setFreshUsers(null); return; }
    api.get('/users/simple', { params: { ids: value.join(','), limit: value.length } })
      .then(r => {
        const list = r.data?.data || [];
        list.forEach(u => { userCache[u.id] = u; });
        setFreshUsers(list);
      })
      .catch(() => setFreshUsers(null));
  }, [open, value.join(',')]);
  // CRITICAL: freshUsers=[] vẫn TRUTHY → `||` sẽ fallback đến []. Chỉ ưu tiên fresh khi
  // có data thật; ngược lại dùng cachedUsers (đã pre-seed stub + currentUser via seedUserCache).
  const users = (freshUsers && freshUsers.length) ? freshUsers : cachedUsers;

  const toggle = (id) => {
    value.includes(id) ? onChange(value.filter(x => x !== id)) : onChange([...value, id]);
    setSearch('');
  };

  const renderAvatar = (u, size = 'sm') => {
    const px = size === 'sm' ? 'w-7 h-7' : 'w-6 h-6';
    const ts = size === 'sm' ? 'text-xs' : 'text-[10px]';
    return u?.avatar
      ? <img src={u.avatar} className={`${px} rounded-full object-cover`} alt={u.name} />
      : <div className={`${px} rounded-full bg-indigo-600 text-white flex items-center justify-center ${ts} font-bold`}>{u?.name?.[0]?.toUpperCase() || '?'}</div>;
  };

  const single = value.length === 1;
  const empty = value.length === 0;

  return (
    <div className="relative" ref={ref}>
      <button type="button"
        onClick={() => { setOpen(!open); setSearch(''); }}
        className={empty
          ? 'w-7 h-7 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition'
          : 'flex items-center gap-1.5 bg-white border border-gray-200 rounded-full pl-1 pr-2 py-0.5 hover:border-blue-300 transition'}
        title={empty ? t`Thêm người thực hiện` : t`Sửa người thực hiện`}>
        {empty && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M12 5v14M5 12h14"/></svg>}
        {single && (() => {
          const u = users[0] || { name: '—' };
          return (
            <>
              {renderAvatar(u)}
              <span className="text-xs text-gray-700 truncate max-w-[160px]">{u.name || '—'}</span>
              {stageLabel && (
                <span className="text-[9px] px-1.5 py-px rounded font-semibold" style={{ backgroundColor: stageColor + '20', color: stageColor }}>{stageLabel}</span>
              )}
            </>
          );
        })()}
        {!single && !empty && (
          <div className="flex items-center -space-x-1.5">
            {users.slice(0, 4).map(u => (
              <div key={u.id} className="ring-2 ring-white rounded-full">
                {renderAvatar(u, 'xs')}
              </div>
            ))}
            {users.length > 4 && (
              <div className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-[10px] font-semibold ring-2 ring-white">+{users.length - 4}</div>
            )}
            <span className="ml-2 text-xs text-gray-600">{users.length} <Trans>người</Trans></span>
          </div>
        )}
      </button>
      {open && ReactDOM.createPortal(
        <FollowersDropdown
          triggerRef={ref}
          followers={users}
          value={value}
          search={search}
          setSearch={setSearch}
          results={results}
          loading={loading}
          labels={lb}
          suggestions={suggestions}
          onClose={() => { setOpen(false); setSearch(''); }}
          onToggle={toggle}
        />,
        document.body,
      )}
    </div>
  );
}

/* Dropdown 2-section: ĐANG THEO DÕI + THÊM NGƯỜI (UX thống nhất với ticket follower) */
function FollowersDropdown({ triggerRef, followers, primaryId, onSetPrimary, value, search, setSearch, results, loading, labels = {}, suggestions = [], onClose, onToggle }) {
  const {
    t
  } = useLingui();

  const title = labels.title || t`Người theo dõi`;
  const currentLabel = labels.current || t`Đang theo dõi`;
  const addLabel = labels.add || t`Thêm người`;
  const followerSet = new Set(value);
  const isActive = (u) => u.is_active !== false;
  // Gợi ý: chỉ hiện khi CHƯA gõ search, loại người đã chọn + đã inactive. CHỈ gợi ý — click mới add.
  const suggested = !search.trim()
    ? suggestions.filter(u => u && !followerSet.has(u.id) && isActive(u))
    : [];
  const matchSearch = (u) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (u.name || '').toLowerCase().includes(q)
      || (u.department || '').toLowerCase().includes(q)
      || (u.position || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q);
  };
  // "Đang theo dõi": GIỮ user đã inactive (đã follow từ trước) — gắn nhãn (vô hiệu) trong row.
  // "Thêm người": chỉ show user active.
  const filteredFollowers = followers.filter(u => matchSearch(u));
  const candidates = results.filter(u => isActive(u) && !followerSet.has(u.id) && matchSearch(u));

  const r = triggerRef.current?.getBoundingClientRect();
  // Căn TRÁI theo mép trigger (giống OwnerPicker cùng file). Trước dùng `r.right - 320`
  // (căn phải) — nhưng trigger là div.relative full-width (block) nên r.right = mép phải
  // cả hàng → popup bị đẩy sang phải rất xa trigger. r.left = nơi avatar/nút "+" bắt đầu.
  const pos = r
    ? { top: r.bottom + 4, left: Math.max(8, Math.min(window.innerWidth - 340 - 8, r.left)) }
    : {};

  return (
    <div className="fixed inset-0 z-[10010]" onClick={onClose}>
      <div className="absolute w-[320px] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
        style={pos} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">{title} ({followers.length})</h3>
          <button onClick={onClose} className="p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-2 border-b border-gray-100">
          <div className="relative">
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} autoFocus
              placeholder={t`Tìm người...`}
              className="w-full px-2 py-1.5 text-sm border-0 focus:outline-none placeholder:text-gray-400 bg-transparent" />
          </div>
        </div>

        <div className="max-h-[360px] overflow-y-auto">
          {/* Section: Đang theo dõi */}
          {filteredFollowers.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{currentLabel}</p>
              {filteredFollowers.map(u => (
                <FollowerRow key={u.id} u={u} mode="remove" onClick={() => onToggle(u.id)}
                  roleLabel={primaryId && String(u.id) === String(primaryId) ? t`Chính` : (primaryId ? t`Đồng` : undefined)}
                  rolePrimary={!!primaryId && String(u.id) === String(primaryId)}
                  onSetPrimary={onSetPrimary && String(u.id) !== String(primaryId) ? () => { onSetPrimary(u.id); onClose(); } : undefined} />
              ))}
            </div>
          )}

          {/* Section: Gợi ý (participant việc cha / hay cộng tác / cùng nhóm) — chỉ khi chưa search */}
          {suggested.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1.5 text-[10px] font-semibold text-blue-400 uppercase tracking-wider"><Trans>Gợi ý</Trans></p>
              {suggested.map(u => (
                <FollowerRow key={`sg-${u.id}`} u={u} mode="add" reasonLabel={u.reason_label} onClick={() => onToggle(u.id)} />
              ))}
            </div>
          )}

          {/* Section: Thêm người */}
          <div>
            <p className="px-4 pt-3 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{addLabel}</p>
            {loading && <div className="py-3 flex justify-center"><Loader2 size={14} className="text-gray-400 animate-spin" /></div>}
            {!loading && candidates.length === 0 && (
              <p className="text-center py-3 text-xs text-gray-400 italic">{search ? t`Không tìm thấy` : t`Hết rồi`}</p>
            )}
            {!loading && candidates.map(u => (
              <FollowerRow key={u.id} u={u} mode="add" onClick={() => onToggle(u.id)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FollowerRow({ u, mode, onClick, reasonLabel, roleLabel, rolePrimary, onSetPrimary }) {
  const sub = u.department || u.position || u.email || '';
  // ⚠️ HAI chỗ vẽ người trong tệp này (`OwnerRow` và đây) — sửa một chỗ là nửa số ô chọn
  // người trong app không có gì đổi, mà nhìn qua thì tưởng xong. Đã cắn 02/09/2026.
  const dongMui = motDongVeMui(u.timezone, u.work_city);
  const inactive = u.is_active === false;
  return (
    <div
      className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left transition group cursor-pointer ${inactive ? 'opacity-70' : ''}`}
      onClick={onClick} role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}>
      {u.avatar
        ? <img src={u.avatar} className={`w-8 h-8 rounded-full object-cover shrink-0 ${inactive ? 'grayscale' : ''}`}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} alt={u.name} />
        : null}
      <div className={`w-8 h-8 rounded-full bg-indigo-600 text-white items-center justify-center text-xs font-bold shrink-0 ${inactive ? 'grayscale' : ''} ${u.avatar ? 'hidden' : 'flex'}`}>
        {u.name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 truncate flex items-center gap-1.5">
          {u.name}
          {/* Badge VAI nằm ở ĐÂY (trong dropdown) chứ không ở header: header cạnh nút Tạo Deal,
              nhồi badge vào là chiếm hết diện tích. */}
          {roleLabel && (
            <span className={`text-[9px] font-semibold px-1.5 py-px rounded shrink-0 border ${rolePrimary
              ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-indigo-50 text-indigo-700 border-indigo-100'}`}>{roleLabel}</span>
          )}
          {reasonLabel && (
            <span className="text-[9px] font-semibold px-1.5 py-px rounded bg-blue-50 text-blue-500 border border-blue-100 shrink-0">{reasonLabel}</span>
          )}
          {inactive && (
            <span className="text-[9px] font-semibold uppercase px-1.5 py-px rounded bg-gray-200 text-gray-500 border border-gray-300 shrink-0"><Trans>Vô hiệu</Trans></span>
          )}
        </p>
        {(sub || dongMui) && (
          <p className="text-[11px] text-gray-400 truncate">
            {sub}
            {sub && dongMui ? ' · ' : ''}
            {dongMui && <span className="text-amber-700">{dongMui}</span>}
          </p>
        )}
      </div>
      {/* "Đặt làm chính" — đổi chủ NGAY trong cùng dropdown, để ô "Sở hữu" chỉ còn MỘT control.
          `stopPropagation` vì hàng cha đang là nút gỡ khỏi danh sách. */}
      {onSetPrimary && (
        <button type="button"
          onClick={e => { e.stopPropagation(); onSetPrimary(); }}
          className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-emerald-200 text-emerald-700 bg-emerald-50 opacity-0 group-hover:opacity-100 hover:bg-emerald-100 transition">
          <Trans>Đặt làm chính</Trans>
        </button>
      )}
      {mode === 'add' ? (
        <Plus size={14} className="text-gray-300 group-hover:text-blue-500 shrink-0 transition" />
      ) : (
        <X size={14} className="text-gray-300 group-hover:text-red-500 shrink-0 transition" />
      )}
    </div>
  );
}
